import React, { useState } from "react";
import { Context } from "@hotwired/stimulus";
import { ReactWrapperController } from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import { cn } from "@root/server/client/esweb/lib/utils.ts";

export const name = "crawl-anything";

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <CrawlAnything {...props} />);
    }
}

type TabType = 'hoofi' | 'apify' | 'scrapy' | 'other';

function CrawlAnything() {
    const [activeTab, setActiveTab] = useState<TabType>('hoofi');

    const tabTitles: Record<TabType, string> = {
        'hoofi': 'extension.js',
        'apify': 'actor.js',
        'scrapy': 'spider.py',
        'other': 'request.sh'
    };

    return (
        <section className="max-w-6xl mx-auto px-4 pb-24 scroll-mt-24 pt-24">
            <div className="text-center mb-16">
                <h3 className="text-4xl max-sm:text-3xl font-bold text-black tracking-tight">
                    Web Crawling
                </h3>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">

                {/* Navigation (Left Column) */}
                <div className="lg:col-span-4 grid grid-cols-2 lg:flex lg:flex-col gap-3">

                    <NavButton
                        isActive={activeTab === 'hoofi'}
                        onClick={() => setActiveTab('hoofi')}
                        theme="indigo"
                        title={<div className="font-bold text-2xl md:text-3xl tracking-tighter leading-none">Hoofi<span className="text-indigo-600">Crawler</span></div>}
                        subtitle="Browser Extension"
                    />

                    <NavButton
                        isActive={activeTab === 'apify'}
                        onClick={() => setActiveTab('apify')}
                        theme="light"
                        title={
                            <img
                                src="/public/logos/apify-light.svg"
                                alt="Apify"
                                className={cn("h-10 w-auto transition-opacity", activeTab === 'apify' ? "opacity-100" : "opacity-100 grayscale hover:grayscale-0")}
                            />
                        }
                        subtitle="Node.js Serverless"
                    />

                    <NavButton
                        isActive={activeTab === 'scrapy'}
                        onClick={() => setActiveTab('scrapy')}
                        theme="light"
                        title={
                            <img
                                src="/public/logos/scrapy.svg"
                                alt="Scrapy"
                                className={cn("h-10 w-auto transition-opacity", activeTab === 'scrapy' ? "opacity-100" : "opacity-100 grayscale hover:grayscale-0")}
                            />
                        }
                        subtitle="Python Framework"
                    />

                    <NavButton
                        isActive={activeTab === 'other'}
                        onClick={() => setActiveTab('other')}
                        theme="light"
                        title={
                            <div className={cn("font-bold text-2xl md:text-3xl tracking-tighter leading-none transition-colors", activeTab === 'other' ? "text-indigo-900" : "text-slate-700 group-hover:text-slate-900")}>
                                Any Source
                            </div>
                        }
                        subtitle="cURL / HTTP"
                    />

                </div>

                {/* Content Pane (Right Column) */}
                <div className="lg:col-span-8">
                    <div className="min-h-[300px] lg:min-h-[400px] flex flex-col relative transition-all duration-300">

                        <div className="flex-1 flex items-stretch justify-center relative">

                            {/* Hoofi Content */}
                            {activeTab === 'hoofi' && (
                                <div className="w-full animate-in fade-in duration-500 flex flex-col px-4 md:px-12 py-0 min-h-[300px]">
                                    <div className="max-w-2xl mx-auto space-y-6 text-slate-900 leading-relaxed text-lg md:text-xl font-medium">
                                        <p>
                                            HoofiCrawler is our secret weapon. It can crawl <b>otherwise borderline impossible</b> pages and bypass the most advanced bot protections, simply by running directly in your browser - <b>it has the same access you do</b>.
                                        </p>
                                        <p>
                                            You have to write the code to extract the data, but HoofiCrawler handles scheduling, deduplication, link following, error handling, monitoring and of course sending data to hoofi.dev.
                                        </p>
                                        <p>
                                            And what is best, it is completely free!
                                        </p>
                                        <div className="pt-4">
                                            <div className="inline-flex items-center gap-4 bg-white border border-slate-200 text-slate-400 py-3.5 px-8 rounded-full font-bold cursor-not-allowed text-base shadow-sm">
                                                <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/Google_Chrome_icon_%28February_2022%29.svg" alt="Chrome" className="w-6 h-6 grayscale opacity-60" />
                                                <span>Add to Chrome (Soon)</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Code Integration Tabs (Shared Layout) */}
                            {activeTab !== 'hoofi' && (
                                <div className="w-full animate-in fade-in duration-500 flex flex-col px-4 md:px-12 py-0 min-h-[300px]">
                                    
                                    {/* Description */}
                                    <div className="max-w-2xl mx-auto space-y-6 text-slate-900 leading-relaxed text-lg md:text-xl font-medium mb-10">
                                        {activeTab === 'apify' && (
                                            <p>Apify allows you to run headless Chrome and scraping scripts in the cloud. You can easily push data to Hoofi from any Apify Actor.</p>
                                        )}
                                        {activeTab === 'scrapy' && (
                                            <p>Scrapy is the industry standard for Python web scraping. Integrate Hoofi by simply sending a request in your spider or pipeline.</p>
                                        )}
                                        {activeTab === 'other' && (
                                            <p>Push data from shell scripts, backend services, or any tool that speaks HTTP. Just send a POST request with your data.</p>
                                        )}
                                    </div>

                                    {/* Code Window */}
                                    <div className="bg-slate-900 rounded-xl overflow-hidden shadow-xl shadow-slate-200 ring-1 ring-slate-900/5 mx-auto w-full max-w-2xl">
                                        <div className="bg-slate-800/50 px-4 py-3 flex items-center gap-2 border-b border-slate-700/50">
                                            <div className="size-3 rounded-full bg-red-500/80"></div>
                                            <div className="size-3 rounded-full bg-yellow-500/80"></div>
                                            <div className="size-3 rounded-full bg-green-500/80"></div>
                                            <div className="flex-1 text-center text-xs font-mono text-slate-500">
                                                {tabTitles[activeTab]}
                                            </div>
                                        </div>
                                        
                                        <div className="p-6 overflow-x-auto">
                                            {activeTab === 'apify' && (
                                                <pre className="text-sm font-mono text-slate-300 whitespace-pre">
                                                    <code className="language-javascript">
{`// In your Apify Actor
await fetch('https://hoofi.dev/filter/YOUR_ID', {
  method: 'POST',
  body: JSON.stringify({ 
    price: 1500, 
    title: "Nice Apartment" 
  }),
  headers: { 'Content-Type': 'application/json' }
});`}
                                                    </code>
                                                </pre>
                                            )}
                                            {activeTab === 'scrapy' && (
                                                <pre className="text-sm font-mono text-slate-300 whitespace-pre">
                                                    <code className="language-python">
{`# In your Spider
yield scrapy.Request(
    'https://hoofi.dev/filter/YOUR_ID',
    method='POST',
    body=json.dumps(item),
    headers={'Content-Type': 'application/json'}
)`}
                                                    </code>
                                                </pre>
                                            )}
                                            {activeTab === 'other' && (
                                                <pre className="text-sm font-mono text-slate-300 whitespace-pre">
                                                    <code className="language-bash">
{`curl -X POST https://hoofi.dev/filter/YOUR_ID \\
  -H "Content-Type: application/json" \\
  -d '{ 
    "price": 25000,
    "currency": "USD" 
  }'`}
                                                    </code>
                                                </pre>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            )}

                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
}

interface NavButtonProps {
    isActive: boolean;
    onClick: () => void;
    title: React.ReactNode;
    subtitle: string;
    theme: 'indigo' | 'light';
}

function NavButton({ isActive, onClick, title, subtitle, theme }: NavButtonProps) {
    
    // Light theme for everyone (except Hoofi which has specific indigo styling in its logic or here)
    // Actually, let's simplify. 
    // Active: bg-indigo-50/50 border-indigo-200 (if indigo) OR bg-white border-slate-200 (if light)
    
    const activeClasses = theme === 'indigo'
        ? "bg-indigo-50/50 text-indigo-900 border-indigo-200 hover:bg-white"
        : "bg-white text-slate-900 border-slate-200 shadow-sm"; // Light active style

    const inactiveClasses = "border-transparent text-slate-600 hover:bg-white hover:text-slate-900";

    const activeSubtitleColor = theme === 'indigo' ? "text-indigo-700/70" : "text-slate-400";

    return (
        <button
            onClick={onClick}
            className={cn(
                "group flex flex-col gap-1 p-4 md:p-5 rounded-xl border transition-all hover:shadow-md text-left relative overflow-hidden",
                isActive ? activeClasses : inactiveClasses
            )}
        >
            <div className="h-10 flex items-center relative z-10">
                {title}
            </div>
            <div className={cn(
                "text-[10px] font-bold uppercase tracking-wider ml-0.5 transition-colors relative z-10",
                isActive ? activeSubtitleColor : "text-slate-400"
            )}>{subtitle}</div>
            
            {/* Visual Indicator/Pointer for Active State */}
            {isActive && (
                <div className={cn(
                    "absolute right-3 top-1/2 -translate-y-1/2 opacity-50 max-lg:hidden",
                    theme === 'indigo' ? "text-indigo-300" : "text-slate-300"
                )}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
            )}
        </button>
    )
}
